import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { AdminUserListDto } from '../types/admin-user.types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import { Textarea } from '@/shared/components/ui/textarea';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

const schema = z.object({
  reason: z.string().min(3, 'Mínimo 3 caracteres.').max(500, 'Máximo 500 caracteres.'),
});

type FormValues = z.infer<typeof schema>;

interface ToggleStatusModalProps {
  user: AdminUserListDto | null;
  isPending: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

export function ToggleStatusModal({ user, isPending, onClose, onConfirm }: ToggleStatusModalProps) {
  const isActivating = user ? !user.isActive : false;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    // react-doctor-disable-next-line react-doctor/no-event-handler -- RHF form.reset on async-loaded detail / controlled-modal prop sync; not derivable in an event handler
    if (!user) form.reset();
  }, [user, form]);

  const onSubmit = (values: FormValues) => {
    onConfirm(values.reason);
  };

  const handleClose = () => {
    if (!isPending) {
      form.reset();
      onClose();
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[32rem]">
        <DialogHeader>
          <DialogTitle>{isActivating ? 'Activar usuario' : 'Desactivar usuario'}</DialogTitle>
          <DialogDescription>
            {isActivating
              ? `Activa la cuenta de ${user?.email}. Se notificará al usuario por correo.`
              : `Desactiva la cuenta de ${user?.email}. Se revocarán todas sus sesiones activas.`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Motivo</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={
                        isActivating
                          ? 'Ej. Cuenta verificada y aprobada por el equipo de operaciones.'
                          : 'Ej. Violación de política de acceso.'
                      }
                      rows={3}
                      disabled={isPending}
                      {...field}
                      data-testid="admin-users-toggle-reason-textarea"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <UnifiedButton
                type="button"
                buttonType="basic"
                variant="default"
                onClick={handleClose}
                disabled={isPending}
                data-testid="admin-users-toggle-cancel-button"
              >
                Cancelar
              </UnifiedButton>
              <UnifiedButton
                type="submit"
                buttonType="basic"
                variant="outline"
                disabled={isPending}
                actionType={'destructive'}
                data-testid="admin-users-toggle-confirm-button"
              >
                {isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isActivating ? 'Activando...' : 'Desactivando...'}
                  </>
                ) : isActivating ? (
                  'Activar'
                ) : (
                  'Desactivar'
                )}
              </UnifiedButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
